// Комментарий на русском: менеджер визуальных эффектов (VFX).
// Управляет глобальным режимом эффектов через атрибут data-vfx на <html>.
// В режиме auto выполняет динамический FPS-тест производительности устройства.

export type VfxMode = 'auto' | 'off' | 'medium' | 'max';
export type VfxTier = 'off' | 'medium' | 'max';

// Комментарий на русском: ключ для localStorage.
const STORAGE_KEY = 'lisad_vfx_auto_tier';
const STORAGE_TTL = 24 * 60 * 60 * 1000; // 24 часа

// Комментарий на русском: пороговые значения FPS для определения tier.
const FPS_THRESHOLD_MAX = 50; // >= 50 FPS → max
const FPS_THRESHOLD_MEDIUM = 30; // >= 30 FPS → medium, < 30 → off

// Комментарий на русском: локальный кэш для текущей сессии.
let sessionCache: VfxTier | null = null;
let fpsTestPromise: Promise<VfxTier> | null = null;

// Комментарий на русском: структура кэша в localStorage.
type StoredTier = {
  tier: VfxTier;
  timestamp: number;
  fps?: number;
};

// Комментарий на русском: получить кэшированный tier из localStorage.
function getStoredTier(): VfxTier | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data: StoredTier = JSON.parse(raw);
    // Проверяем TTL
    if (Date.now() - data.timestamp > STORAGE_TTL) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data.tier;
  } catch {
    return null;
  }
}

// Комментарий на русском: сохранить tier в localStorage.
function storeTier(tier: VfxTier, fps?: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const data: StoredTier = { tier, timestamp: Date.now(), fps };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

// Комментарий на русском: быстрые эвристики без FPS-теста.
function getQuickHeuristics(): VfxTier | null {
  if (typeof window === 'undefined') return 'medium';

  // Проверка prefers-reduced-motion (приоритет пользователя)
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    return 'off';
  }

  // Комментарий на русском: на мобильных устройствах отключаем VFX по умолчанию для экономии ресурсов.
  const isMobile = window.matchMedia?.('(max-width: 768px)')?.matches ||
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (isMobile) {
    return 'off';
  }

  const nav = navigator as any;

  // Проверка deviceMemory (Chrome/Edge) — очень слабые устройства
  const memory = nav.deviceMemory;
  if (typeof memory === 'number' && memory < 2) {
    return 'off';
  }

  // Проверка connection (режим экономии данных)
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  if (conn?.saveData) {
    return 'off';
  }

  // Не определено однозначно — нужен FPS-тест
  return null;
}

// Комментарий на русском: выполнить FPS-тест (измерение производительности).
// Рисуем тяжёлую анимацию 300ms и считаем кадры.
async function runFpsTest(): Promise<number> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      resolve(60); // fallback
      return;
    }
    const raf = requestAnimationFrame;
    const caf = cancelAnimationFrame;

    const testDuration = 300; // ms
    let frameCount = 0;
    let startTime: number | null = null;
    let rafId: number;

    // Комментарий на русском: создаём тестовый элемент с тяжёлыми эффектами.
    const testEl = document.createElement('div');
    testEl.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 200px;
      height: 200px;
      background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
      backdrop-filter: blur(20px);
      box-shadow: 0 25px 50px rgba(0,0,0,0.5), 0 0 100px rgba(59,130,246,0.3);
      border-radius: 50%;
      transform: rotate(0deg) scale(1);
      opacity: 1;
    `;
    document.body.appendChild(testEl);

    let rotation = 0;
    let scale = 1;
    let direction = 1;

    const measure = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp;
      }

      frameCount++;

      // Комментарий на русском: анимируем тяжёлые свойства для нагрузки GPU/CPU.
      rotation += 5;
      scale += 0.02 * direction;
      if (scale > 1.3 || scale < 0.7) direction *= -1;

      testEl.style.transform = `rotate(${rotation}deg) scale(${scale})`;
      testEl.style.boxShadow = `0 ${25 + Math.sin(rotation * 0.1) * 10}px 50px rgba(0,0,0,0.5)`;

      const elapsed = timestamp - startTime;
      if (elapsed < testDuration) {
        rafId = raf(measure);
      } else {
        // Комментарий на русском: тест завершён, вычисляем FPS.
        document.body.removeChild(testEl);
        const fps = Math.round((frameCount * 1000) / elapsed);
        resolve(fps);
      }
    };

    rafId = raf(measure);

    // Комментарий на русском: timeout на случай зависания.
    setTimeout(() => {
      if (startTime === null) {
        caf(rafId);
        try { document.body.removeChild(testEl); } catch {}
        resolve(30); // fallback medium
      }
    }, testDuration + 500);
  });
}

// Комментарий на русском: определить tier по результату FPS-теста.
function fpsToTier(fps: number): VfxTier {
  if (fps >= FPS_THRESHOLD_MAX) return 'max';
  if (fps >= FPS_THRESHOLD_MEDIUM) return 'medium';
  return 'off';
}

// Комментарий на русском: синхронное определение tier (с кэшем).
export function detectDeviceTier(): VfxTier {
  // Проверяем кэш сессии
  if (sessionCache) return sessionCache;

  // Проверяем localStorage
  const stored = getStoredTier();
  if (stored) {
    sessionCache = stored;
    return stored;
  }

  // Быстрые эвристики
  const heuristic = getQuickHeuristics();
  if (heuristic) {
    sessionCache = heuristic;
    storeTier(heuristic);
    return heuristic;
  }

  // Если нет кэша — возвращаем medium как безопасный дефолт
  // FPS-тест запустится асинхронно
  return 'medium';
}

// Комментарий на русском: асинхронное определение tier с FPS-тестом.
export async function detectDeviceTierAsync(): Promise<VfxTier> {
  // Проверяем кэш сессии
  if (sessionCache) return sessionCache;

  // Проверяем localStorage
  const stored = getStoredTier();
  if (stored) {
    sessionCache = stored;
    return stored;
  }

  // Быстрые эвристики (приоритетные случаи)
  const heuristic = getQuickHeuristics();
  if (heuristic) {
    sessionCache = heuristic;
    storeTier(heuristic);
    return heuristic;
  }

  // Если тест уже запущен — ждём его
  if (fpsTestPromise) return fpsTestPromise;

  // Запускаем FPS-тест
  fpsTestPromise = (async () => {
    try {
      const fps = await runFpsTest();
      const tier = fpsToTier(fps);
      sessionCache = tier;
      storeTier(tier, fps);
      return tier;
    } catch {
      // Fallback на эвристики
      const nav = navigator as any;
      const memory = nav.deviceMemory;
      const cores = nav.hardwareConcurrency;
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      let tier: VfxTier = 'medium';
      if (typeof memory === 'number' && memory >= 8 && typeof cores === 'number' && cores >= 8 && !isMobile) {
        tier = 'max';
      } else if (typeof memory === 'number' && memory < 4 || isMobile) {
        tier = 'medium';
      }

      sessionCache = tier;
      storeTier(tier);
      return tier;
    } finally {
      fpsTestPromise = null;
    }
  })();

  return fpsTestPromise;
}

// Комментарий на русском: применить tier к документу.
export function applyVfxTier(tier: VfxTier): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-vfx', tier);
}

// Комментарий на русском: получить текущий tier из DOM.
export function getCurrentVfxTier(): VfxTier | null {
  if (typeof document === 'undefined') return null;
  const val = document.documentElement.getAttribute('data-vfx');
  if (val === 'off' || val === 'medium' || val === 'max') return val;
  return null;
}

// Комментарий на русском: преобразовать mode в tier (синхронно).
export function modeToTier(mode: VfxMode): VfxTier {
  if (mode === 'auto') return detectDeviceTier();
  return mode;
}

// Комментарий на русском: преобразовать mode в tier (асинхронно с FPS-тестом).
export async function modeToTierAsync(mode: VfxMode): Promise<VfxTier> {
  if (mode === 'auto') return detectDeviceTierAsync();
  return mode;
}

// Комментарий на русском: сброс кэша (для тестов/отладки).
export function resetAutoTierCache(): void {
  sessionCache = null;
  fpsTestPromise = null;
  if (typeof localStorage !== 'undefined') {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }
}

// Комментарий на русском: получить информацию о текущем состоянии (для отладки).
export function getVfxDebugInfo(): {
  sessionCache: VfxTier | null;
  storedTier: VfxTier | null;
  currentDom: VfxTier | null;
  heuristics: VfxTier | null;
} {
  return {
    sessionCache,
    storedTier: getStoredTier(),
    currentDom: getCurrentVfxTier(),
    heuristics: getQuickHeuristics(),
  };
}
